import React, { useEffect, useState } from "react";
import { listActions, retryAction, resolveAction, sendToReview } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import DetailDrawer from "@/components/DetailDrawer";
import { toast } from "sonner";
import { RotateCw, Check, Inbox } from "lucide-react";

export default function Failures() {
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState({});

    const load = async () => {
        const rows = await listActions("failed");
        setItems(rows);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    const run = async (id, fn, label) => {
        setBusy((b) => ({ ...b, [id]: true }));
        try {
            await fn(id);
            toast.success(`${label} · ${id.slice(0, 8)}`);
            await load();
        } catch (e) {
            toast.error(`${label} failed`);
        } finally {
            setBusy((b) => ({ ...b, [id]: false }));
        }
    };

    return (
        <div data-testid="failures-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff453a] mb-2">
                    // triage
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Failures</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Actionable. Retry, resolve, or route to human review.
                </p>
            </div>

            <div className="border border-[#262a2e] bg-[#111315]">
                {items.length === 0 && (
                    <div className="p-16 text-center" data-testid="failures-empty">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c]">
                            all clear
                        </div>
                        <div className="font-mono text-[11px] text-[#9ca3af] mt-2">
                            No failures to triage.
                        </div>
                    </div>
                )}

                {items.map((a) => (
                    <div
                        key={a.id}
                        className="pc-row px-5 py-4 border-b last:border-b-0 border-[#262a2e] flex items-start gap-4 cursor-pointer"
                        onClick={() => {
                            setSelected(a);
                            setOpen(true);
                        }}
                        data-testid={`failure-row-${a.id}`}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1.5">
                                <StatusPill status={a.status} />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                                    {a.type}
                                </span>
                                <span className="font-mono text-[10px] text-[#9ca3af]">
                                    retry ×{a.retry_count}
                                </span>
                            </div>
                            <div className="font-mono text-[12px] text-[#f3f4f6] truncate">
                                {a.target}
                            </div>
                            <div className="font-mono text-[11px] text-[#ff453a] mt-1 truncate">
                                {a.error || "(no error message)"}
                            </div>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <ActionBtn
                                onClick={() => run(a.id, retryAction, "Retried")}
                                disabled={busy[a.id]}
                                icon={RotateCw}
                                label="Retry"
                                accent="#7cc93c"
                                testid={`failure-retry-${a.id}`}
                            />
                            <ActionBtn
                                onClick={() => run(a.id, resolveAction, "Resolved")}
                                disabled={busy[a.id]}
                                icon={Check}
                                label="Resolve"
                                accent="#9ca3af"
                                testid={`failure-resolve-${a.id}`}
                            />
                            <ActionBtn
                                onClick={() => run(a.id, sendToReview, "Sent to review")}
                                disabled={busy[a.id]}
                                icon={Inbox}
                                label="Review"
                                accent="#ff9f0a"
                                testid={`failure-review-${a.id}`}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <DetailDrawer action={selected} open={open} onOpenChange={setOpen} />
        </div>
    );
}

function ActionBtn({ onClick, disabled, icon: Icon, label, accent, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-[2px] border border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6] disabled:opacity-40 transition-colors"
            style={{ ["--a"]: accent }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.color = accent;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#262a2e";
                e.currentTarget.style.color = "#9ca3af";
            }}
        >
            <Icon className="w-3 h-3" />
            {label}
        </button>
    );
}
